import { Injectable, Injector } from "@angular/core";
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  interval,
  tap,
} from "rxjs";
import { HttpClient } from "@angular/common/http";
import { environment } from "src/environments/environment";

import { SessionSocketService } from "./session-socket.service";

import { OverlayService } from "./overlay.service";
import { SessionService } from "./session.service";
import { SessionAlertService } from "./session-alert.service";
import { PlayBackService } from "./playback.service";
import { ToasterService } from "./toaster.service";

import { noop } from "rxjs";
import { SofyLiveService } from "./sofylive.service";
import { UtilsService } from "./utils.services";
import {
  MANUAL_TESTRUN_RESULTS,
  TIMEREVENTS,
} from "src/app/shared/constants/constants";
import { DataDogService } from "./dataDog.service";

@Injectable({
  providedIn: "root",
})
export class TimerService {
  hours: number;
  minutes: number;
  seconds: number;
  testId: any;
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  timer$: Observable<number>;
  timerSubscription: Subscription;
  timerEvents$: Subject<string> = new Subject<string>();
  httpOptions = {};
  isSessionEnded: boolean = false;
  sessionReleasedForNegativeTimer: boolean = false;
  sessionReleasedForTimerComplete: boolean = false;

  private alert5MinSent = false;
  private alert1MinSent = false;
  private alert30sSent = false;

  private targetTimestamp: number;

  constructor(
    private httpClient: HttpClient,
    private injector: Injector,
    private sofyLive: SofyLiveService,
    private sessionSocket: SessionSocketService,
    private utils: UtilsService,
    private overlayService: OverlayService,
    private datadogService: DataDogService,
    public sessionService: SessionService,
    private sessionAlert: SessionAlertService
  ) {
    
    this.sofyLive.issueCreateModal_ModelData.subscribe((result) => {
      this.testId = result ? result.TestCaseResultId : null;
    });
    this.httpOptions = this.utils.httpOptions;
  }

  setSeconds(_seconds: number) {
    if (_seconds > 59) throw new Error("Invalid Seconds");
    this.seconds = _seconds;
  }

  setMinutes(_minutes: number) {
    if (_minutes > 59) throw new Error("Invalid Minutes");
    this.minutes = _minutes;
  }

  setHours(_hours: number) {
    this.hours = _hours;
  }
  requestSessionAlertPermission() {
    this.sessionAlert.requestPermission();
  }

  startTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    const totalRemainingSeconds =
      this.hours * 3600 + this.minutes * 60 + this.seconds;

    // If we're already at or below zero, end immediately instead of
    // waiting for the interval tick.
    if (totalRemainingSeconds <= 0) {
      this.datadogService.sendMessage(`Total Remaining time is ${totalRemainingSeconds}. ${this.sessionService.SessionGUID}`, {
      });
      this.handleTimerComplete();
      return;
    }
    this.targetTimestamp = Date.now() + totalRemainingSeconds * 1000;

    this.timer$ = interval(1000).pipe(
      tap(() => this.updateTimeFromSystemClock())
    );

    this.timerSubscription = this.timer$.subscribe();
    this.timerEvents$.next(TIMEREVENTS.STARTED_SESSION);

    this.sessionAlert.requestPermission().catch(() => {});
  }

  private updateTimeFromSystemClock() {
    const now = Date.now();
    let remainingMs = this.targetTimestamp - now;
    if (remainingMs <= 0) remainingMs = 0;

    const totalSeconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setHours(hours);
    this.setMinutes(minutes);
    this.setSeconds(seconds);

    if (totalSeconds <= 300 && totalSeconds > 0 && !this.alert5MinSent) {
      this.alert5MinSent = true;
      this.sessionAlert.alertUser(
        "Your Sofy device session will expire in 5 minutes."
      );
    }
    if (totalSeconds <= 60 && totalSeconds > 0 && !this.alert1MinSent) {
      this.alert1MinSent = true;
      this.sessionAlert.alertUser(
        "Your Sofy device session will expire in 1 minute."
      );
    }
    if (totalSeconds <= 30 && totalSeconds > 0 && !this.alert30sSent) {
      this.alert30sSent = true;
      this.sessionAlert.alertUser(
        "Your Sofy device session will expire in 30 seconds."
      );
    }
    
    if (remainingMs <= 0 && !this.sessionReleasedForTimerComplete) {
      this.datadogService.sendMessage(`Remaining time in session on Frontend Computation ${remainingMs}. ${this.sessionService.SessionGUID}`, {
      });
      this.sessionReleasedForTimerComplete = true;
      this.handleTimerComplete();
    }
  }

  handleTimerComplete() {
    this.timerSubscription?.unsubscribe();
    
    const playbackService = this.injector.get(PlayBackService);
    const sessionSocketService = this.injector.get(SessionSocketService);
    this.datadogService.sendMessage(`Session expired on timer end. Terminating all Runs. ${this.sessionService.SessionGUID}`, {
    });
    playbackService.ForceStopAllPlayBacks();
    sessionSocketService.SendForceStopPlaybacks();
  

    if (this.testId != null) {
      this.sessionSocket.SendManualEnd(
        this.testId,
        MANUAL_TESTRUN_RESULTS.CANCELLED
      );
    }

    this.timerEvents$.next(TIMEREVENTS.COMPLETED_SESSION);
    this.releaseSessionWithEffect();
  }

  releaseSessionWithEffect() {
    this.sessionService.ReleaseSessionWithEffect().subscribe(() => {
      this.isSessionEnded = true;
      this.sessionAlert.clearTabFlashing()
      this.overlayService.SetOverlayText("Session Expired");
      this.overlayService.HideLoading();
      this.overlayService.ShowOverlay();
    });
  }

  ResetTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.hours = 0;
    this.minutes = 0;
    this.seconds = 0;
    this.timerEvents$.next(TIMEREVENTS.RESET_SESSION);
  }

  Init(SessionGUID: string): void {
    this.GetSessionRemainingTime(SessionGUID).subscribe(
      (Response) => {
        const remaining = Response?.data?.RemainingTime ?? 0;

        if (Response.data.Expired || remaining <= 0) {
          this.datadogService.sendMessage(`Remaining time ${remaining} seconds of session from API Response. Session Status ${Response.data.Expired}. ${this.sessionService.SessionGUID}`, {
          });
          if (this.testId != null)
            this.sessionSocket.SendManualEnd(
              this.testId,
              MANUAL_TESTRUN_RESULTS.CANCELLED
            );
          this.isSessionEnded = true;
          this.handleTimerComplete();

          this.timerEvents$.next(TIMEREVENTS.ENDED_SESSION);
          return;
        }

        this.setHours(this.CalculateTimeData(remaining, 1));
        this.setMinutes(this.CalculateTimeData(remaining, 2));
        this.setSeconds(this.CalculateTimeData(remaining, 3));
      },
      (_err: any) => noop,
      () => {
        if (!this.isSessionEnded) {
          this.startTimer();
        }
      }
    );
  }

  CalculateTimeData(RemainingTime: number, flag: number): any {
    switch (flag) {
      case 1:
        return Math.floor(RemainingTime / 3600);
      case 2:
        return Math.floor((RemainingTime % 3600) / 60);
      case 3:
        return RemainingTime % 60;
    }
  }

  ExtendSession(SessionGUID: string): void {
    if (this.isLoading.value) return;

    
    if (this.isSessionEnded || this.sessionReleasedForTimerComplete) {
      const toasterService = this.injector.get(ToasterService);
      toasterService.ShowToastMessage(
        "Session already ended. Please start a new session to continue.",
        0,
        4000
      );

      this.releaseSessionWithEffect();
      return;
    }

    const body = {
      userGuid:
        this.sessionService.sessionData.getValue().ParentGUID ===
        "00000000-0000-0000-0000-000000000000"
          ? this.sessionService.UserGUID
          : this.sessionService.sessionData.getValue().ParentGUID,
    };

    this.isLoading.next(true);
    this.ExtendIDESession(SessionGUID, body).subscribe(
      (res) => {
        this.timerSubscription?.unsubscribe();
        this.timer$ = null;

        this.setHours(0);
        this.setMinutes(0);
        this.setSeconds(0);

        // reset alert flags
        this.alert5MinSent = false;
        this.alert1MinSent = false;
        this.alert30sSent = false;

        setTimeout(() => {
          const h = this.CalculateTimeData(res.data.remainingTime, 1);
          const m = this.CalculateTimeData(res.data.remainingTime, 2);
          const s = this.CalculateTimeData(res.data.remainingTime, 3);
          this.setHours(h);
          this.setMinutes(m);
          this.setSeconds(s);
          this.startTimer();
        }, 10);

        this.isLoading.next(false);
        this.sessionAlert.clearTabFlashing();
      },
      (error) => {
        if (error?.error?.message === "Device Hour Limit Exceeded") {
          const toasterService = this.injector.get(ToasterService);
          toasterService.ShowToastMessage(
            "Device Hour Limit Exceeded!",
            0,
            2000
          );
        } else {
         
          const toasterService = this.injector.get(ToasterService);
          toasterService.ShowToastMessage(
            "Unable to extend the session. It may have already ended.",
            0,
            4000
          );

         
          this.releaseSessionWithEffect();
        }
        this.isLoading.next(false);
      }
    );
  }
 

  private ExtendIDESession(sessionGUID: string, body: any): Observable<any> {
    return this.httpClient.put<any>(
      `${environment.baseUrl}/session-microservice/session/${sessionGUID}/extend`,
      body,
      this.httpOptions
    );
  }

  GetSessionRemainingTime(sessionGUID: string): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.baseUrl}/session-microservice/session/${sessionGUID}/time`,
      this.httpOptions
    );
  }
}
